import * as React from "react"
import { useEffect, useState } from "react"
import { get, isEmpty } from "lodash"
import { Box, Button, TextAreaFormField } from "@indeed/ifl-components"
import { ChevronDown, ChevronUp } from "@indeed/ifl-icons"
import { useAppDispatch, useAppSelector } from "../../../common/utils/redux-helpers"
import { decryptInfo, encryptInfo } from "./encryption-api"
import { InfoConversionStatus } from "./test-hub-model"
import { decryptedInfoSelector, encryptedInfoSelector, getEncryptedInfoFailure } from "./test-hub-slices"

export const TestHubInfoConverter: React.FC = () => {
    const dispatch = useAppDispatch()
    const { data: decryptedInfo, error: decryptionError, status: decryptionStatus } = useAppSelector(decryptedInfoSelector)
    const { data: encryptedInfo, error: encryptionError, status: encryptionStatus } = useAppSelector(encryptedInfoSelector)

    const [info, setInfo] = useState<string | null>(null)
    const [decryptedInfoString, setDecryptedInfoString] = useState("")
    const [showDecryptor, setShowDecryptor] = useState(false)

    const disableDecryptButton = isEmpty(info)
    const disableEncryptButton = isEmpty(decryptedInfoString)
    const isDecrypting = decryptionStatus === InfoConversionStatus.LOADING
    const isEncrypting = encryptionStatus === InfoConversionStatus.LOADING

    useEffect(() => {
        setDecryptedInfoString(decryptedInfo ? JSON.stringify(decryptedInfo) : "")
    }, [decryptedInfo])

    useEffect(() => {
        setInfo(encryptedInfo)
    }, [encryptedInfo])

    const handleDecryptorToggle = () => {
        setShowDecryptor(!showDecryptor)
    }

    const handleDecrypt = () => {
        // call API only if encryptedInfo is not empty
        if (isEmpty(info)) {
            return
        }
        dispatch(decryptInfo(info!))
    }

    const handleEncrypt = () => {
        // call API only if decryptedInfo is not empty
        if (isEmpty(decryptedInfoString)) {
            return
        }

        let decryptedInfoObj = null
        try {
            decryptedInfoObj = JSON.parse(decryptedInfoString)
        } catch (err) {
            const msg = get(err, "message", "An error occurred")
            dispatch(
                getEncryptedInfoFailure({
                    error: msg,
                })
            )
        }

        if (decryptedInfoObj) {
            dispatch(encryptInfo(decryptedInfoObj))
        }
    }

    return (
        <>
            <Button
                iconAfter={showDecryptor ? <ChevronUp /> : <ChevronDown />}
                onClick={handleDecryptorToggle}
                variant="secondary"
                type="button"
            >
                {showDecryptor ? "Hide Info Converter" : "Show Info Converter"}
            </Button>
            {showDecryptor && (
                <Box
                    data-testid="converter-container"
                    sx={{
                        border: "1px solid",
                        borderColor: "border",
                        marginBlockStart: 3,
                        paddingBlockStart: 2,
                        paddingBlockEnd: 2,
                        paddingInlineStart: 2,
                        paddingInlineEnd: 2,
                        inlineSize: "100%",
                    }}
                >
                    <TextAreaFormField
                        errorText={decryptionError}
                        invalid={Boolean(decryptionError)}
                        label="Info param"
                        sx={{
                            marginBlockStart: 3,
                            marginBlockEnd: 3,
                            textarea: {
                                fontSize: 1,
                            },
                            inlineSize: "100%",
                        }}
                        textareaProps={{
                            onChange: (e) => {
                                setInfo(e.target.value.trim())
                            },
                            onKeyPress: (e) => {
                                if (e.key === "Enter") {
                                    handleDecrypt()
                                }
                            },
                            value: info || "",
                        }}
                    />
                    <Button
                        disabled={disableDecryptButton}
                        loading={isDecrypting}
                        onClick={handleDecrypt}
                        size="sm"
                        variant="secondary"
                        type="button"
                    >
                        Decrypt
                    </Button>
                    <TextAreaFormField
                        errorText={encryptionError}
                        invalid={Boolean(encryptionError)}
                        label="Decrypted info"
                        sx={{
                            marginBlockStart: 3,
                            marginBlockEnd: 3,
                            textarea: {
                                fontSize: 1,
                            },
                            inlineSize: "100%",
                        }}
                        textareaProps={{
                            onChange: (e) => {
                                setDecryptedInfoString(e.target.value.trim())
                            },
                            onKeyPress: (e) => {
                                if (e.key === "Enter") {
                                    handleEncrypt()
                                }
                            },
                            value: decryptedInfoString,
                        }}
                    />
                    <Button
                        disabled={disableEncryptButton}
                        loading={isEncrypting}
                        onClick={handleEncrypt}
                        size="sm"
                        variant="secondary"
                        type="button"
                    >
                        Encrypt
                    </Button>
                </Box>
            )}
        </>
    )
}
